/**
 * Ce qu'a donné la dernière série reçue, sur la carte « à commander » de la page
 * Réappro (Philippe, 07/10/2026) : vendu, perdu, prélevé, et la marge avant et
 * après pertes — pour recommander autant, moins, ou pas du tout.
 *
 * Le calcul est fait par le serveur (api/src/restock-alerts/serie-precedente.ts).
 */
import { formatQty } from '../../utils/pos.utils';

export interface ResumeSerie {
  recue_le: string;
  fournisseur: string | null;
  au_poids: boolean;
  unite: string | null;
  nb_lots: number;
  recu: number;
  vendu: number;
  perdu: number;
  preleve: number;
  ecart: number;
  en_stock: number;
  /** Résidu du bouclage reçu = vendu + perdu + prélevé ± écart + stock (0 = complet). */
  sans_trace: number;
  ca_ht_cents: number;
  vendu_sans_prix: number;
  cout_pertes_cents: number;
  cout_prelevements_cents: number;
  marge_cents: number | null;
  marge_pct: number | null;
  marge_apres_pertes_cents: number | null;
  marge_apres_pertes_pct: number | null;
  taux_perte: number | null;
  cout_inconnu: boolean;
  production_propre: boolean;
}

/** Couleur des pertes : au-delà de 15 % de la série, on a trop commandé. */
export function niveauPerte(taux: number | null): 'ok' | 'attention' | 'alerte' {
  if (taux == null || taux < 5) return 'ok';
  return taux < 15 ? 'attention' : 'alerte';
}

const euros = (cents: number) =>
  (cents / 100).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const COULEURS = {
  ok: 'text-gray-700',
  attention: 'text-amber-700',
  alerte: 'text-red-700',
};

export default function BilanSeriePrecedente({ serie, titre = 'Dernière série' }: { serie: ResumeSerie; titre?: string }) {
  const u = serie.au_poids ? ' kg' : '';
  const date = new Date(serie.recue_le).toLocaleDateString('fr-BE', { day: 'numeric', month: 'numeric' });
  const niveau = niveauPerte(serie.taux_perte);
  const disparu = serie.ecart < 0 ? -serie.ecart : 0;

  return (
    <div className="mt-1.5 rounded-md bg-slate-50 border border-slate-200 px-2 py-1 text-xs text-slate-700">
      <div>
        <span className="font-semibold">{titre}</span> reçue le {date}
        {serie.fournisseur ? ` (${serie.fournisseur})` : ''} :{' '}
        <span className="tabular-nums">
          {formatQty(serie.recu)}
          {u} reçus · <b>{formatQty(serie.vendu)}</b> vendus
          {serie.perdu > 0 && (
            <span className={COULEURS[niveau]}> · <b>{formatQty(serie.perdu)}</b> perdus</span>
          )}
          {disparu > 0 && (
            <span className={COULEURS[niveau]}> · {formatQty(disparu)} manquants au comptage</span>
          )}
          {serie.preleve > 0 && <> · {formatQty(serie.preleve)} prélevés</>}
          {serie.en_stock > 0 && <> · {formatQty(serie.en_stock)} encore en stock</>}
          {serie.sans_trace > 0 && (
            <span className="text-slate-500"> · {formatQty(serie.sans_trace)} sortis sans trace</span>
          )}
        </span>
        {serie.taux_perte != null && serie.taux_perte > 0 && (
          <span className={`font-semibold ${COULEURS[niveau]}`}> ({serie.taux_perte.toLocaleString('fr-BE')} % de pertes)</span>
        )}
      </div>
      <div className="tabular-nums">
        {serie.production_propre ? (
          <>Notre production — CA {euros(serie.ca_ht_cents)} HT (récolte sans prix d’achat, pas de marge)</>
        ) : serie.marge_pct == null ? (
          <>
            CA {euros(serie.ca_ht_cents)} HT
            {serie.cout_inconnu ? ' — prix d’achat manquant, marge non calculable' : ''}
          </>
        ) : (
          <>
            Marge <b>{serie.marge_pct.toLocaleString('fr-BE')} %</b>
            {(serie.cout_pertes_cents > 0 || serie.cout_prelevements_cents > 0) &&
              serie.marge_apres_pertes_cents != null && (
                <>
                  {' '}→ après pertes{serie.cout_prelevements_cents > 0 ? ' et prélèvements' : ''}{' '}
                  {serie.marge_apres_pertes_cents < 0 ? (
                    // Un pourcentage d'un CA minuscule ne veut rien dire (« −4 872 % ») :
                    // ce qui parle, c'est ce que la série a coûté.
                    <b className="text-red-700">perte nette de {euros(-serie.marge_apres_pertes_cents)}</b>
                  ) : (
                    <>
                      <b>{(serie.marge_apres_pertes_pct ?? 0).toLocaleString('fr-BE')} %</b>{' '}
                      ({euros(serie.marge_apres_pertes_cents)})
                    </>
                  )}
                </>
              )}
            <span className="text-slate-500"> · CA {euros(serie.ca_ht_cents)} HT</span>
          </>
        )}
        {serie.vendu_sans_prix > 0 && (
          <span className="text-slate-500"> · {formatQty(serie.vendu_sans_prix)}{u} vendus sans prix retrouvé</span>
        )}
      </div>
    </div>
  );
}
